import {
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  startOfMonth
} from 'date-fns'
import type {
  Day
} from 'date-fns'
import type {
  PublicSchedulingLinkResponse,
  SchedulingSlotResponse
} from '../../client'

export const GUEST_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

interface WeekInfo {
  weekend: number[]
}

// Intl exposes week data as getWeekInfo() or, before that was standardised, a
// weekInfo getter. Weekdays run 1 (Monday) through 7 (Sunday).
type LocaleWithWeekInfo = Intl.Locale & {
  getWeekInfo?: () => WeekInfo,
  weekInfo?: WeekInfo
}

// IANA zones of the countries whose weekend is Friday–Saturday (CLDR), so their
// work week runs Sunday to Thursday. A guest's browser language rarely carries
// the country (an Israeli guest on en-US reports nothing Israeli), but their
// time zone does. It is the browser zone, not a `?tz=` display override: the
// browser says where the guest is, the override only which clock to show.
// Legacy links (Asia/Tel_Aviv, Israel, Egypt, Libya) cover an engine that
// reports the OS zone uncanonicalised.
const SUNDAY_TO_THURSDAY_TIMEZONES = new Set([
  'Africa/Algiers',
  'Africa/Cairo',
  'Africa/Khartoum',
  'Africa/Tripoli',
  'Asia/Aden',
  'Asia/Amman',
  'Asia/Baghdad',
  'Asia/Bahrain',
  'Asia/Damascus',
  'Asia/Jerusalem',
  'Asia/Kuwait',
  'Asia/Muscat',
  'Asia/Qatar',
  'Asia/Riyadh',
  'Asia/Tel_Aviv',
  'Egypt',
  'Israel',
  'Libya'
])

function hasFridaySaturdayWeekend(locale: string): boolean {
  try {
    const loc = new Intl.Locale(locale) as LocaleWithWeekInfo
    const weekend = loc.getWeekInfo?.().weekend ?? loc.weekInfo?.weekend
    return weekend?.length === 2 && weekend.includes(5) && weekend.includes(6)
  } catch {
    return false
  }
}

// First column of the booking grid (0 = Sunday, date-fns convention). Monday
// for everyone, so Sunday sits beside Saturday: US guests read a leading Sunday
// as the start of the work week and booked Sundays by mistake. The exception is
// a guest whose time zone or locale puts them in a Sunday-to-Thursday work
// week, who would read Sunday at the end of the row as the weekend.
export function weekStartsOnForGuest(locale: string, timeZone: string): Day {
  return SUNDAY_TO_THURSDAY_TIMEZONES.has(timeZone) || hasFridaySaturdayWeekend(locale) ? 0 : 1
}

export const GUEST_WEEK_STARTS_ON = weekStartsOnForGuest(
  Intl.DateTimeFormat().resolvedOptions().locale,
  GUEST_TIMEZONE
)

export function isValidTimezone(tz: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

export function formatTime(isoString: string, timezone: string): string {
  return new Date(isoString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone
  })
}

export function formatTimezoneName(tz: string): string {
  return tz.replace(/_/g, ' ').replace(/\//g, ' / ')
}

// Civil date (YYYY-MM-DD) of a slot in the given timezone. en-CA renders the
// ISO-style YYYY-MM-DD ordering with zero-padding, so the same string doubles
// as a stable map key and as something we can parse back into a local Date.
export function toDateKey(isoString: string, timezone: string): string {
  return new Date(isoString).toLocaleDateString('en-CA', { timeZone: timezone })
}

export function groupSlotsByDateKey(
  slots: SchedulingSlotResponse[],
  timezone: string
): Record<string, SchedulingSlotResponse[]> {
  const groups: Record<string, SchedulingSlotResponse[]> = {}
  for (const slot of slots) {
    const key = toDateKey(slot.start_time, timezone)
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(slot)
  }
  return groups
}

// "2026-06-17" -> a local Date at midnight. Parsed component-wise so it is
// never shifted by the runtime's UTC offset the way `new Date("2026-06-17")`
// (which parses as UTC) would be.
export function dateKeyToLocalDate(dateKey: string): Date {
  const parts = dateKey.split('-')
  const year = Number(parts[0])
  const month = Number(parts[1])
  const day = Number(parts[2])
  return new Date(year, month - 1, day)
}

export function localDateToKey(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

export function formatDayLabelFromKey(dateKey: string): string {
  return format(dateKeyToLocalDate(dateKey), 'EEEE, MMMM d')
}

export interface MonthGridDay {
  date: Date
  dateKey: string
}

// Cells for the visible month plus the leading blanks needed to align the 1st
// under the correct weekday column of a grid starting on `weekStartsOn`.
export function buildMonthGrid(monthCursor: Date, weekStartsOn: Day): {
  leadingBlanks: number
  days: MonthGridDay[]
} {
  const start = startOfMonth(monthCursor)
  const end = endOfMonth(monthCursor)
  const days = eachDayOfInterval({ start, end }).map((date) => ({
    date,
    dateKey: localDateToKey(date)
  }))
  return { leadingBlanks: (getDay(start) - weekStartsOn + 7) % 7, days }
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0] ?? ''
  if (!first) return '?'
  if (parts.length === 1) return first.slice(0, 2).toUpperCase()
  const last = parts[parts.length - 1] ?? ''
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase()
}

// The single-line "with ..." host descriptor, mirroring how each link type
// names its host(s) on the booking page.
export function formatHostLine(
  link: PublicSchedulingLinkResponse
): string | null {
  const hostNames = (link.hosts ?? [])
    .map((h) => h.name)
    .filter((name): name is string => Boolean(name))
  if (link.link_type === 'collective' && hostNames.length > 1) {
    const last = hostNames[hostNames.length - 1]
    const rest = hostNames.slice(0, -1)
    return `with ${rest.join(', ')} & ${last}`
  }
  if (link.link_type === 'round_robin' && hostNames.length > 1) {
    return `with one of ${hostNames.join(', ')}`
  }
  if (link.owner_name) {
    return `with ${link.owner_name}`
  }
  return null
}

// --- Visitor-availability overlay ("show times you're free") ---

export interface BusyInterval {
  // ISO instants (any offset); compared in epoch ms, so no tz math needed.
  start: string
  end: string
}

export type SlotAvailability = 'free' | 'busy'

// Marks each slot busy iff it overlaps any of the visitor's busy intervals.
// Half-open overlap (start < busyEnd && end > busyStart) so back-to-back
// meetings don't mark the adjacent slot.
export function computeSlotAvailability(
  slots: SchedulingSlotResponse[],
  busyIntervals: BusyInterval[]
): Map<number, SlotAvailability> {
  const busy = busyIntervals
    .map((b) => ({ start: Date.parse(b.start), end: Date.parse(b.end) }))
    .filter((b) => Number.isFinite(b.start) && Number.isFinite(b.end))
  const result = new Map<number, SlotAvailability>()
  for (const slot of slots) {
    const slotStart = Date.parse(slot.start_time)
    const slotEnd = Date.parse(slot.end_time)
    const isBusy = busy.some((b) => slotStart < b.end && slotEnd > b.start)
    result.set(slot.id, isBusy ? 'busy' : 'free')
  }
  return result
}

// The [min slot start, max slot end] window the visitor's calendar must be
// queried over. Null when there are no slots (nothing to query).
export function slotHorizon(
  slots: SchedulingSlotResponse[]
): { start: Date, end: Date } | null {
  let min = Infinity
  let max = -Infinity
  for (const slot of slots) {
    const start = Date.parse(slot.start_time)
    const end = Date.parse(slot.end_time)
    if (Number.isFinite(start)) min = Math.min(min, start)
    if (Number.isFinite(end)) max = Math.max(max, end)
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) return null
  return { start: new Date(min), end: new Date(max) }
}

const HORIZON_CHUNK_DAYS = 60

// Splits a horizon into ≤60-day windows: Google's freebusy API caps the
// queryable range, and chunking also bounds a single Graph calendarView page
// walk. Rolling links rarely exceed one chunk.
export function chunkHorizon(
  horizon: { start: Date, end: Date }
): Array<{ start: Date, end: Date }> {
  const chunkMs = HORIZON_CHUNK_DAYS * 24 * 60 * 60 * 1000
  const chunks: Array<{ start: Date, end: Date }> = []
  let cursor = horizon.start.getTime()
  const end = horizon.end.getTime()
  while (cursor < end) {
    const next = Math.min(cursor + chunkMs, end)
    chunks.push({ start: new Date(cursor), end: new Date(next) })
    cursor = next
  }
  return chunks
}

// A single initials avatar only makes sense when there is one identifiable
// host; multi-host collective/round-robin links return null so the rail skips
// the avatar rather than implying one person.
export function singleHostName(
  link: PublicSchedulingLinkResponse
): string | null {
  const multiHost =
    (link.link_type === 'collective' || link.link_type === 'round_robin') &&
    (link.hosts?.length ?? 0) > 1
  return multiHost ? null : link.owner_name ?? null
}
